import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import FileUploader from "@/components/FileUploader";
import { toast } from "sonner";
import { fmtMoney, CURRENCY_OPTIONS } from "@/lib/format";

const UNITS = ["Adet", "KG", "Litre", "Koli", "Paket", "Kutu", "Metre", "Rulo", "Palet"];

const Field = ({ label, required, error, children }) => (
  <div>
    <label className="block text-xs font-semibold text-[#111827] mb-1.5">
      {label} {required && <span className="text-[#DC2626]">*</span>}
    </label>
    {children}
    {error && <div className="text-xs text-[#DC2626] mt-1">{error}</div>}
  </div>
);

export default function ProductForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    product_code: "",
    product_name: "",
    description: "",
    category_id: "",
    brand_id: "",
    unit: "Adet",
    current_stock: 0,
    critical_stock_level: 0,
    purchase_price: 0,
    sales_markup_percent: 30,
    minimum_markup_percent: 10,
    source_type: "ready",
    currency: "TRY",
    supplier_name: "",
    notes: "",
    images: [],
  });

  useEffect(() => {
    api.get("/definitions/categories").then((r) => setCategories(r.data.filter(c => c.is_active)));
    api.get("/definitions/brands").then((r) => setBrands(r.data.filter(b => b.is_active)));
    if (isEdit) {
      api.get(`/products/${id}`).then((r) => setForm({ ...r.data })).catch(() => {
        toast.error("Ürün bulunamadı");
        navigate("/products");
      });
    }
  }, [id, isEdit, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const salesPrice = (Number(form.purchase_price) * (1 + Number(form.sales_markup_percent) / 100)).toFixed(2);
  const minPrice = (Number(form.purchase_price) * (1 + Number(form.minimum_markup_percent) / 100)).toFixed(2);

  const submit = async (e) => {
    e.preventDefault();
    if (Number(form.minimum_markup_percent) >= Number(form.sales_markup_percent)) {
      toast.error("Minimum kar yüzdesi, satış kar yüzdesinden düşük olmalı");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        current_stock: Number(form.current_stock),
        critical_stock_level: Number(form.critical_stock_level),
        purchase_price: Number(form.purchase_price),
        sales_markup_percent: Number(form.sales_markup_percent),
        minimum_markup_percent: Number(form.minimum_markup_percent),
        images: form.images || [],
      };
      if (isEdit) {
        await api.put(`/products/${id}`, payload);
        toast.success("Ürün güncellendi");
      } else {
        await api.post("/products", payload);
        toast.success("Ürün oluşturuldu");
      }
      navigate("/products");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5 max-w-5xl">
      <div className="azd-card p-6">
        <div className="text-sm font-bold text-[#08285A] mb-4 uppercase tracking-wider">
          Temel Bilgiler
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Ürün Kodu" required>
            <Input data-testid="product-code" value={form.product_code}
                   onChange={(e) => set("product_code", e.target.value)} required />
          </Field>
          <Field label="Ürün Adı" required>
            <Input data-testid="product-name" value={form.product_name}
                   onChange={(e) => set("product_name", e.target.value)} required />
          </Field>
          <div className="md:col-span-2">
            <Field label="Ürün Açıklaması" required>
              <Textarea data-testid="product-description" value={form.description}
                        onChange={(e) => set("description", e.target.value)} rows={3} required />
            </Field>
          </div>
          <Field label="Kategori" required>
            <Select value={form.category_id} onValueChange={(v) => set("category_id", v)}>
              <SelectTrigger data-testid="product-category"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Marka" required>
            <Select value={form.brand_id} onValueChange={(v) => set("brand_id", v)}>
              <SelectTrigger data-testid="product-brand"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>
                {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Birim" required>
            <Select value={form.unit} onValueChange={(v) => set("unit", v)}>
              <SelectTrigger data-testid="product-unit"><SelectValue /></SelectTrigger>
              <SelectContent>
                {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Ürün Kaynağı" required>
            <Select value={form.source_type} onValueChange={(v) => set("source_type", v)}>
              <SelectTrigger data-testid="product-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="production">Üretim</SelectItem>
                <SelectItem value="ready">Hazır Alım</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Para Birimi" required>
            <Select value={form.currency || "TRY"} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger data-testid="product-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CURRENCY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </div>

      <div className="azd-card p-6">
        <div className="text-sm font-bold text-[#08285A] mb-4 uppercase tracking-wider">
          Stok ve Fiyatlandırma
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label={isEdit ? "Mevcut Stok (sadece görüntüleme)" : "İlk Stok Miktarı"} required>
            <Input data-testid="product-stock" type="number" min="0" step="0.01"
                   value={form.current_stock} disabled={isEdit}
                   onChange={(e) => set("current_stock", e.target.value)} required />
          </Field>
          <Field label="Kritik Stok Seviyesi" required>
            <Input data-testid="product-critical" type="number" min="0" step="0.01"
                   value={form.critical_stock_level}
                   onChange={(e) => set("critical_stock_level", e.target.value)} required />
          </Field>
          <Field label="Alış Fiyatı (KDV Hariç)" required>
            <Input data-testid="product-purchase" type="number" min="0" step="0.01"
                   value={form.purchase_price}
                   onChange={(e) => set("purchase_price", e.target.value)} required />
          </Field>
          <Field label="Satış Kar Yüzdesi (%)" required>
            <Input data-testid="product-markup" type="number" min="0" step="0.01"
                   value={form.sales_markup_percent}
                   onChange={(e) => set("sales_markup_percent", e.target.value)} required />
          </Field>
          <Field label="Minimum Kar Yüzdesi (%)" required>
            <Input data-testid="product-min-markup" type="number" min="0" step="0.01"
                   value={form.minimum_markup_percent}
                   onChange={(e) => set("minimum_markup_percent", e.target.value)} required />
          </Field>
          <div className="md:col-span-3 grid md:grid-cols-2 gap-4 bg-[#F6F8FB] p-4 rounded-lg border border-[#E5E7EB]">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">
                Otomatik Satış Fiyatı
              </div>
              <div className="text-xl font-extrabold text-[#0B3676]" data-testid="calc-sales-price">
                {fmtMoney(salesPrice, form.currency)}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">
                Minimum Satış Fiyatı
              </div>
              <div className="text-xl font-extrabold text-[#F59E0B]" data-testid="calc-min-price">
                {fmtMoney(minPrice, form.currency)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="azd-card p-6">
        <div className="text-sm font-bold text-[#08285A] mb-4 uppercase tracking-wider">
          Ürün Görselleri (Opsiyonel)
        </div>
        <p className="text-xs text-[#6B7280] mb-3">
          Maksimum 5 görsel yükleyebilirsiniz. JPG, PNG, WEBP desteklenir.
        </p>
        <FileUploader
          value={form.images || []}
          onChange={(v) => set("images", v)}
          category="products"
          accept="image/jpeg,image/png,image/webp,image/jpg"
          maxFiles={5}
          label="Görsel Yükle"
          testid="product-image-upload"
        />
      </div>

      <div className="azd-card p-6">
        <div className="text-sm font-bold text-[#08285A] mb-4 uppercase tracking-wider">
          Opsiyonel Bilgiler
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Tedarikçi (Hazır Alım için)">
            <Input data-testid="product-supplier" value={form.supplier_name}
                   onChange={(e) => set("supplier_name", e.target.value)} />
          </Field>
          <Field label="İç Not (Müşteriye görünmez)">
            <Input data-testid="product-notes" value={form.notes}
                   onChange={(e) => set("notes", e.target.value)} />
          </Field>
        </div>
      </div>

      <div className="flex gap-3 justify-end">
        <Button type="button" variant="outline" onClick={() => navigate("/products")}
                data-testid="cancel-btn">
          Vazgeç
        </Button>
        <Button type="submit" disabled={saving} data-testid="save-product"
                className="bg-[#0B3676] hover:bg-[#08285A] text-white">
          {saving ? "Kaydediliyor..." : isEdit ? "Güncelle" : "Kaydet"}
        </Button>
      </div>
    </form>
  );
}
